import { supabase } from '@/lib/supabase'

export type ResultImage = {
  id: number
  resultId: number
  url: string
  storageBucket: string | null
  storagePath: string | null
  contentType: string | null
  sizeBytes: number | null
  createdAt: string
}

export type ResultEntry = {
  id: number
  opportunityId: number
  authorId: string
  authorName: string
  authorAvatarUrl: string | null
  createdAt: string
  images: ResultImage[]
}

export async function fetchResultsEnabled(): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('user_results_enabled' as any)
    if (error) {
      // eslint-disable-next-line no-console
      console.error('user_results_enabled error', error)
      return false
    }
    return Boolean(data)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('user_results_enabled error', e)
    return false
  }
}

export async function fetchResultsThread(opportunityId: number): Promise<ResultEntry[]> {
  const oppId = Number(opportunityId)
  if (!Number.isFinite(oppId)) return []

  const { data, error } = await supabase
    .from('opportunity_results' as any)
    .select(
      'id,opportunity_id,author_id,author_name,author_avatar_url,created_at,images:opportunity_result_images(id,result_id,url,storage_bucket,storage_path,content_type,size_bytes,created_at)',
    )
    .eq('opportunity_id', oppId)
    .order('created_at', { ascending: true })

  if (error) {
    // eslint-disable-next-line no-console
    console.error('fetchResultsThread error', error)
    return []
  }

  const rows = Array.isArray(data) ? data : []

  return rows.map((row: any) => {
    const imagesRaw = Array.isArray(row.images) ? row.images : []
    const images: ResultImage[] = imagesRaw.map((img: any) => ({
      id: Number(img.id),
      resultId: Number(img.result_id),
      url: String(img.url || ''),
      storageBucket:
        typeof img.storage_bucket === 'string' || img.storage_bucket === null
          ? (img.storage_bucket as string | null)
          : null,
      storagePath:
        typeof img.storage_path === 'string' || img.storage_path === null
          ? (img.storage_path as string | null)
          : null,
      contentType:
        typeof img.content_type === 'string' || img.content_type === null
          ? (img.content_type as string | null)
          : null,
      sizeBytes:
        img.size_bytes === null || img.size_bytes === undefined
          ? null
          : Number.isFinite(Number(img.size_bytes))
            ? Number(img.size_bytes)
            : null,
      createdAt: img.created_at ? String(img.created_at) : '',
    }))

    const authorName = String(row.author_name || '').trim() || 'Usuário'

    return {
      id: Number(row.id),
      opportunityId: Number(row.opportunity_id),
      authorId: row.author_id ? String(row.author_id) : '',
      authorName,
      authorAvatarUrl:
        typeof row.author_avatar_url === 'string' || row.author_avatar_url === null
          ? (row.author_avatar_url as string | null)
          : null,
      createdAt: row.created_at ? String(row.created_at) : '',
      images,
    }
  })
}

export async function postResult(
  opportunityId: number,
  authorName?: string | null,
  authorAvatarUrl?: string | null,
): Promise<ResultEntry | null> {
  const oppId = Number(opportunityId)
  if (!Number.isFinite(oppId)) return null

  const auth = await supabase.auth.getUser()
  const user = auth.data.user
  const userId = user?.id
  if (!userId) throw new Error('Usuário não autenticado')

  const meta = (user?.user_metadata || {}) as any
  const rawName = authorName || (meta.full_name as string | null) || (user.email as string | null)
  const finalName = String(rawName || '').trim() || 'Usuário'

  let finalAvatar: string | null = null
  if (typeof authorAvatarUrl === 'string') {
    finalAvatar = authorAvatarUrl
  } else if (authorAvatarUrl === null) {
    finalAvatar = null
  } else if (typeof meta.avatar_url === 'string' || meta.avatar_url === null) {
    finalAvatar = meta.avatar_url as string | null
  }

  const { data, error } = await supabase
    .from('opportunity_results' as any)
    .insert({
      opportunity_id: oppId,
      author_id: userId,
      author_name: finalName,
      author_avatar_url: finalAvatar ?? null,
    } as any)
    .select('id,opportunity_id,author_id,author_name,author_avatar_url,created_at')
    .single()

  if (error) {
    // eslint-disable-next-line no-console
    console.error('postResult error', error)
    throw error
  }

  const row: any = data

  return {
    id: Number(row.id),
    opportunityId: Number(row.opportunity_id),
    authorId: row.author_id ? String(row.author_id) : '',
    authorName: String(row.author_name || '').trim() || 'Usuário',
    authorAvatarUrl:
      typeof row.author_avatar_url === 'string' || row.author_avatar_url === null
        ? (row.author_avatar_url as string | null)
        : null,
    createdAt: row.created_at ? String(row.created_at) : '',
    images: [],
  }
}

export async function addResultImages(
  resultId: number,
  items: {
    url: string
    storageBucket?: string | null
    storagePath?: string | null
    contentType?: string | null
    sizeBytes?: number | null
  }[],
): Promise<ResultImage[]> {
  const id = Number(resultId)
  if (!Number.isFinite(id) || id <= 0) return []

  const rows = (items || [])
    .map((item) => {
      const url = String(item.url || '').trim()
      if (!url) return null
      return {
        result_id: id,
        url,
        storage_bucket: item.storageBucket ?? null,
        storage_path: item.storagePath ?? null,
        content_type: item.contentType ?? null,
        size_bytes:
          typeof item.sizeBytes === 'number' && Number.isFinite(item.sizeBytes)
            ? Math.max(0, Math.floor(item.sizeBytes))
            : item.sizeBytes == null
              ? null
              : Number(item.sizeBytes) || null,
      }
    })
    .filter(Boolean) as any[]

  if (!rows.length) return []

  const { data, error } = await supabase
    .from('opportunity_result_images' as any)
    .insert(rows as any)
    .select(
      'id,result_id,url,storage_bucket,storage_path,content_type,size_bytes,created_at',
    )

  if (error) {
    // eslint-disable-next-line no-console
    console.error('addResultImages error', error)
    throw error
  }

  const out: ResultImage[] = Array.isArray(data)
    ? (data as any[]).map((row) => ({
        id: Number(row.id),
        resultId: Number(row.result_id),
        url: String(row.url || ''),
        storageBucket:
          typeof row.storage_bucket === 'string' || row.storage_bucket === null
            ? (row.storage_bucket as string | null)
            : null,
        storagePath:
          typeof row.storage_path === 'string' || row.storage_path === null
            ? (row.storage_path as string | null)
            : null,
        contentType:
          typeof row.content_type === 'string' || row.content_type === null
            ? (row.content_type as string | null)
            : null,
        sizeBytes:
          row.size_bytes === null || row.size_bytes === undefined
            ? null
            : Number.isFinite(Number(row.size_bytes))
              ? Number(row.size_bytes)
              : null,
        createdAt: row.created_at ? String(row.created_at) : '',
      }))
    : []

  return out
}

export async function deleteResult(resultId: number): Promise<void> {
  const id = Number(resultId)
  if (!Number.isFinite(id) || id <= 0) return

  const { error } = await supabase
    .from('opportunity_results' as any)
    .delete()
    .eq('id', id)

  if (error) {
    // eslint-disable-next-line no-console
    console.error('deleteResult error', error)
    throw error
  }
}

